import React, { Component } from 'react';
import ImageGallery, { FlowShot } from '../Imgpop';
import FeatureCards from '../FeatureCards';
import TechTags from '../TechTags';
import "./style.css"

class LeadIdentity extends Component {
    render() {
        const highlightCards = [
            {
                title: 'Known Visitor Personalization',
                description: 'Recognized visitors can skip already completed information, see campus-aware options, and continue enrollment, and tours from a shared HubSpot identity rather than starting over on every page.',
            },
            {
                title: 'Connected Lead Journeys',
                description: 'Enrollment, request-information, website forms, campus tours, and campaign activity share one identity layer so a lead can move between experiences without fragmenting across systems.',
            },
            {
                title: 'CRM + Analytics Connection',
                description: 'Website behavior, form submissions, and bookings stay tied to HubSpot contacts while GA4 and GTM receive the same identity context for attribution.',
            },
        ];

        const analyticsCards = [
            {
                title: 'GA4 → HubSpot',
                description: 'The GA4 Client ID is written to HubSpot so CRM records can be associated with the same anonymous-to-known website session that generated the lead.',
            },
            {
                title: 'HubSpot → GA4',
                description: 'The HubSpot Contact ID is passed back to GA4 as user_id so later site activity is attributed to the resolved contact rather than a disconnected anonymous user.',
            },
            {
                title: 'GTM Data Layer',
                description: 'Lead, campus, and application context is pushed through the Google Tag Manager data layer so analytics and marketing tags receive a consistent identity payload.',
            },
        ];

        const reliabilityCards = [
            {
                title: 'Server-Side CRM Integration',
                description: 'Contact lookup, create, and update happen on the server so HubSpot remains the authoritative identity source and browser-submitted values cannot quietly rewrite another visitor’s record.',
            },
            {
                title: 'Caching & API Protection',
                description: 'Repeated HubSpot lookups and campus or school data requests are cached and rate-protected so personalization stays fast without exhausting API limits.',
            },
            {
                title: 'Graceful Fallbacks',
                description: 'If CRM or analytics services are unavailable, forms still collect information and the visitor experience degrades to a complete, unpersonalized flow rather than blocking conversion.',
            },
        ];

        const identitySpine = [
            'Website Visitor',
            'Identity Resolution',
            'HubSpot Contact',
        ];

        const identityBranches = [
            'Enrollment',
            'Request Information',
            'Website Forms',
            'Tour Booking',
            'Analytics',
        ];

        const galleryImages = [
            {
                src: require('./img/lead-identity/enroll-now-zip-code.png'),
                title: 'Identify the visitor',
                alt: 'Start your enrollment modal with name, email, phone, and ZIP code fields',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/lead-identity/rfi-school-grade.png'),
                title: 'Complete remaining details',
                alt: 'Request Information form greeting a returning visitor with nearby campuses and grade selection',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/lead-identity/journey.png'),
                title: 'Continue the journey',
                alt: 'Choose Your Next Step page with Apply, Take a Tour, and Attend an Event options',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/lead-identity/return-visitor.png'),
                title: 'Returning visitor',
                alt: 'Request Information form greeting a returning visitor with nearby campuses and grade selection',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/lead-identity/New User tour.png'),
                title: 'New visitor tour',
                alt: 'Schedule an in-person tour form for a new visitor with contact and ZIP fields',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/lead-identity/returning_user_tour.png'),
                title: 'Returning visitor tour',
                alt: 'Schedule an in-person tour calendar for a recognized visitor greeted by name',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
        ];

        const enrollmentSteps = [
            {
                ...galleryImages[0],
                step: 1,
                index: 0,
                caption: 'New visitors provide initial information while recognized contacts can continue using known profile data.',
            },
            {
                ...galleryImages[1],
                step: 2,
                index: 1,
                caption: 'Previously completed information can be skipped or prefilled so the visitor only provides what is still needed.',
            },
            {
                ...galleryImages[2],
                step: 3,
                index: 2,
                caption: 'Completed information determines the appropriate next step in the state-specific enrollment journey.',
            },
        ];

        const tourFlow = [
            'Tour Form',
            'GP Booking',
            'HubSpot Booking',
            'Contact + School',
        ];

        return (
            <div className='collapsible'>
                <h1>Lead Journey &amp; Identity Platform</h1>
                <div className="gradient"></div>
                <p className='eyebrow mt-4'>A connected lead-conversion experience across WordPress, HubSpot, Gravity Forms, GA4, tours, and events</p>
                <ImageGallery images={galleryImages}>
                    <div className="details">
                        <p>The Lead Journey &amp; Identity Platform is a custom integration layer that connects public-facing enrollment, request-information, forms, campus tours, and campaign activity to a single HubSpot contact.</p>
                        <p>WordPress and Gravity Forms provide the visitor-facing experiences. Behind them, identity resolution, campus context, CRM writes, and analytics handoffs keep known visitors, new leads, and returning families on one continuous journey rather than a collection of disconnected forms.</p>
                        <p>The work is less about a single interface and more about the shared contact architecture that lets enrollment, forms, bookings, and GA4 all operate against the same person.</p>
                        <div className="hero-video">
                            <video
                                controls
                                playsInline
                                preload="metadata"
                                aria-label="Lead journey enrollment experience"
                                src={require('./img/lead-identity/enroll-now.mp4')}
                            >
                                Your browser does not support the video tag.
                            </video>
                        </div>
                        <p className="shot-caption">A personalized enrollment experience that connects website interactions with CRM data and lead journeys.</p>
                        <p className='eyebrow mt-4'>Platform at a Glance</p>
                        <FeatureCards className="mt-4 mb-4" items={highlightCards} />
                        <p className='eyebrow mt-4'>Connecting the Lead Journey</p>
                        <p>A shared identity layer sits between the website and HubSpot. When a visitor is recognized, the same contact object can personalize enrollment, forms, tours, and analytics. When they are new, the first eligible conversion creates that contact and subsequent experiences continue from it.</p>
                        <p>Identity is resolved server-side using HubSpot as the source of truth. Front-end integrations do not each invent their own visitor matching; they consume the resolved contact and campus context for the current session.</p>
                        <div className="identity-map" aria-label="Shared identity architecture">
                            <ol className="sync-diagram identity-map-spine">
                                {identitySpine.map((step) => (
                                    <li key={step} className="sync-diagram-step">
                                        <span className="sync-diagram-card">{step}</span>
                                    </li>
                                ))}
                            </ol>
                            <ul className="identity-map-branches">
                                {identityBranches.map((item) => (
                                    <li key={item} className="sync-diagram-card">{item}</li>
                                ))}
                            </ul>
                        </div>
                        <p className="shot-caption">A shared identity layer connects visitor activity with personalized forms, enrollment journeys, tours, and analytics.</p>
                        <div className="feature-card">
                            <p className="eyebrow">Progressive Enrollment Experiences</p>
                            <p>Enrollment and request-information flows are staged so known visitors can skip completed profile data while new visitors provide only what is needed to continue. State, campus, and remaining questions adapt as identity and preferences become available.</p>
                            <p>The result is a coordinated multi-stage journey rather than a single monolithic form, with the next step determined by what is already known about the contact.</p>
                            <div className="wizard-flow">
                                {enrollmentSteps.map((item) => (
                                    <div key={item.title}>
                                        <FlowShot
                                            src={item.src}
                                            alt={item.alt}
                                            title={item.title}
                                            step={item.step}
                                            index={item.index}
                                            variant={item.variant}
                                            className={item.className}
                                        />
                                        <p className="shot-caption">{item.caption}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <p className='eyebrow mt-4'>Smart Forms Across the Website</p>
                        <div className="row mt-3 align-items-center">
                            <div className='col-sm-5'>
                                <p>Eligible Gravity Forms can open an identity gate before the visitor fills the remaining fields. Known contacts receive prefills and skipped questions from HubSpot; unknown visitors complete the minimum required identity details first.</p>
                                <p>Forms stay visually native to the public website. The personalization is the shared identity service, not a HubSpot-hosted form embedded in place of the page.</p>
                                <p>HubSpot remains the authoritative identity source, so website forms do not become a competing record of who the visitor is.</p>
                            </div>
                            <div className='col-sm-7'>
                                <FlowShot {...galleryImages[3]} index={3} layout="single" />
                            </div>
                        </div>
                        <p className='eyebrow mt-4'>Connecting Website Behavior to HubSpot &amp; GA4</p>
                        <p>Lead conversion is only useful if later website activity can be attributed to the same person. The integration layer writes the GA4 Client ID into HubSpot and returns the HubSpot Contact ID to GA4 as user_id, with lead, campus, and application context available through GTM.</p>
                        <FeatureCards className="mt-4 mb-4" items={analyticsCards} />
                        <div className="integration-map" aria-label="Two-way GA4 and HubSpot identity bridge">
                            <div className="sync-diagram-card">Website / GA4</div>
                            <span className="integration-map-link" aria-hidden="true">⇄</span>
                            <div className="integration-map-core">
                                <div className="sync-diagram-card">Integration Layer</div>
                                <p>GTM Data Layer</p>
                                <p>Lead / campus / application context</p>
                            </div>
                            <span className="integration-map-link" aria-hidden="true">⇄</span>
                            <div className="sync-diagram-card">HubSpot</div>
                        </div>
                        <ul className="integration-map-notes">
                            <li>GA4 Client ID → HubSpot contact</li>
                            <li>HubSpot Contact ID → GA4 user_id</li>
                        </ul>
                        <p className="shot-caption">A two-way identity bridge connects CRM contacts with website analytics and attribution.</p>
                        <div className="feature-card">
                            <p className="eyebrow">Connecting Tour Bookings to the CRM</p>
                            <p>Take a Tour uses the same identity resolution as enrollment and website forms. Gravity Forms collects the appointment, GP Bookings stores the slot, and HubSpot receives the booking object associated with the contact and school.</p>
                            <div className="row mt-3">
                                <div className='col-sm-6'>
                                    <FlowShot {...galleryImages[4]} index={4} />
                                </div>
                                <div className='col-sm-6'>
                                    <FlowShot {...galleryImages[5]} index={5} />
                                </div>
                            </div>
                            <p className="shot-caption">The tour scheduling experience extends the same visitor identity into real-world campus interactions.</p>
                            <ol className="sync-diagram" aria-label="Tour booking CRM flow">
                                {tourFlow.map((step) => (
                                    <li key={step} className="sync-diagram-step">
                                        <span className="sync-diagram-card">{step}</span>
                                    </li>
                                ))}
                            </ol>
                            <p className="shot-caption mb-0">Appointments are synchronized with HubSpot and connected to the appropriate contact and school records.</p>
                        </div>
                        <p className='eyebrow mt-4'>Campus-Aware Experiences</p>
                        <p>Campus context can come from existing HubSpot preferences, ZIP-based nearby results, an explicit campus selection, URL parameters, or site configuration.</p>
                        <p>Once a campus is known, enrollment, smart forms, and tour scheduling stay aligned with that school instead of asking the visitor to reselect it on every step.</p>
                        <div className="lead-narrow">
                            <p className='eyebrow mt-4'>Event &amp; Campaign Attribution</p>
                            <p>Eventbrite and campaign activity are attributed back to the same HubSpot contact rather than treated as a separate marketing silo. The interesting work is the join between event participation, website identity, and CRM records, not the Eventbrite interface itself.</p>
                        </div>
                        <p className='eyebrow mt-4'>Built for Reliability &amp; Data Protection</p>
                        <FeatureCards className="mt-4 mb-4" items={reliabilityCards} />
                        <p className="eyebrow mt-4">My Role</p>
                        <div className="lead-narrow lead-narrow-flush">
                            <p>I designed and developed the identity and conversion layer that connects WordPress, Gravity Forms, HubSpot, GA4/GTM, GP Bookings, campus data, and Eventbrite attribution.</p>
                            <p>The work included visitor identity resolution, progressive enrollment and request-information journeys, smart-form personalization, two-way GA4 and HubSpot identity bridging, tour booking synchronization, campus-aware context, API caching and fallbacks, and keeping HubSpot as the authoritative contact record.</p>
                            <p>I worked closely with leadership, marketing, and Business Intelligence teams to translate enrollment, lead-conversion, attribution, and reporting needs into technical solutions that could work across the organization’s existing platforms and workflows.</p>
                        </div>
                        <TechTags
                            items={['PHP', 'JavaScript', 'WordPress', 'Gravity Forms', 'REST APIs', 'HubSpot', 'GA4', 'Google Tag Manager', 'GP Bookings', 'Eventbrite']}
                        />
                    </div>
                </ImageGallery>
            </div>
        )
    }
}

export default LeadIdentity;
